/**
 * Address Field Configuration Utilities
 * 
 * Provides functions for managing address field configurations,
 * validation, and rendering based on type_id requirements
 */

export const BOND_NO_MAX_LENGTH = 125;

export function clampBondNoMaxLength(value) {
	if (value === '' || value == null) return value;
	const numeric = Number(value);
	if (!Number.isFinite(numeric)) return '';
	return Math.min(Math.max(numeric, 0), BOND_NO_MAX_LENGTH);
}

export function truncateBondNo(value) {
	if (value == null || value === '') return value;
	return String(value).slice(0, BOND_NO_MAX_LENGTH);
}

export function wasBondNoTruncated(value) {
	if (value == null || value === '') return false;
	return String(value).length > BOND_NO_MAX_LENGTH;
}

export const BOND_NO_TRUNCATED_MESSAGE =
	`This bond number was longer than ${BOND_NO_MAX_LENGTH} characters and has been shortened. Saving this form will keep the shortened value.`;

export function normalizeAddressBondNo(address) {
	if (!address || typeof address !== 'object') return address;
	if (!('bond_no' in address)) return address;
	return {
		...address,
		bond_no: truncateBondNo(address.bond_no),
	};
}

/**
 * Transform database field_config records to component state format
 * @param {Array} dbConfigs - Array of field_config records from database
 * @param {Number} typeId - Address type_id for defaults
 * @returns {Object} - Object with fields array in component format
 */
export function transformDbConfigToState(dbConfigs, typeId) {
	if (!dbConfigs || dbConfigs.length === 0) {
		// Return defaults if no configs exist
		return { fields: getDefaultFieldsForType(typeId) };
	}
	
	const fields = dbConfigs.map(config => ({
		id: config.id,
		key: config.key,
		type: config.type,
		label: config.label,
		defaultLabel: config.default_label,
		placeholder: config.placeholder || '',
		visible: config.visible,
		required: config.required,
		systemRequired: config.system_required,
		order: config.order,
		width: config.width || 'col',
		helpText: config.help_text || '',
		minLength: config.min_length,
		maxLength: config.max_length,
		pattern: config.pattern || '',
		expanded: false // UI state, not from DB
	})).sort((a, b) => a.order - b.order);
	
	return { fields };
}

/**
 * Transform component state to database field_config records format
 * @param {Array} stateFields - Array of field objects in component format
 * @param {Number} fieldId - Parent field ID
 * @returns {Array} - Array of field_config records ready for database
 */
export function transformStateToDbConfig(stateFields, fieldId) {
	if (!stateFields || stateFields.length === 0) {
		return [];
	}
	
	return stateFields.map(field => {
		const config = {
			field_id: fieldId,
			key: field.key,
			type: field.type,
			label: field.label,
			default_label: field.defaultLabel || field.label,
			placeholder: field.placeholder || null,
			visible: field.visible,
			required: field.required,
			system_required: field.systemRequired || false,
			order: field.order,
			width: field.width || 'col',
			help_text: field.helpText || null,
			min_length: field.minLength || null,
			max_length: field.key === 'bond_no'
				? (clampBondNoMaxLength(field.maxLength) || null)
				: (field.maxLength || null),
			pattern: field.pattern || null
		};
		
		// Include id if it exists (for updates)
		if (field.id) {
			config.id = field.id;
		}
		
		return config;
	});
}

/**
 * Get required field keys based on type_id
 * These are the minimum system-required fields for validation
 */
export function getSystemRequiredFields(typeId) {
	// Project Location (type_id = 2) - no company/contact info
	if (typeId === 2) {
		return ['address', 'city', 'state', 'zip'];
	}
	
	// All other address types - require company and basic address
	return ['company', 'address', 'city', 'zip'];
}

/**
 * Validate address data against field configuration
 */
export function validateAddressWithConfig(addressData, fieldConfig, typeId) {
	const errors = [];
	const systemRequired = getSystemRequiredFields(typeId);
	
	if (!fieldConfig || !fieldConfig.fields) {
		// Fallback to system requirements if no config
		systemRequired.forEach(key => {
			if (!addressData[key] || addressData[key].trim() === '') {
				errors.push({ field: key, message: `${key} is required` });
			}
		});
		if (addressData.bond_no && addressData.bond_no.length > BOND_NO_MAX_LENGTH) {
			errors.push({
				field: 'bond_no',
				message: `Bond Number must be no more than ${BOND_NO_MAX_LENGTH} characters`
			});
		}
		return errors;
	}

	// Check each configured field
	fieldConfig.fields.forEach(field => {
		if (!field.visible) return;

		const value = addressData[field.key];

		if (field.required) {
			if (!value || (typeof value === 'string' && value.trim() === '')) {
				errors.push({
					field: field.key,
					message: `${field.label} is required`
				});
			}
		}

		if (value && field.type === 'text') {
			if (field.minLength && value.length < field.minLength) {
				errors.push({
					field: field.key,
					message: `${field.label} must be at least ${field.minLength} characters`
				});
			}
			const maxLength = field.key === 'bond_no'
				? (clampBondNoMaxLength(field.maxLength) || BOND_NO_MAX_LENGTH)
				: field.maxLength;
			if (maxLength && value.length > maxLength) {
				errors.push({
					field: field.key,
					message: `${field.label} must be no more than ${maxLength} characters`
				});
			}
		}
	});

	return errors;
}

/**
 * Get visible fields in order from configuration - DEPRECATED
 */
// export function getVisibleFields(fieldConfig) {
// 	if (!fieldConfig || !fieldConfig.fields) {
// 		return [];
// 	}
	
// 	return fieldConfig.fields
// 		.filter(field => field.visible)
// 		.sort((a, b) => a.order - b.order);
// }

/**
 * Get field configuration by key
 */
export function getFieldByKey(fieldConfig, key) {
	if (!fieldConfig || !fieldConfig.fields) {
		return null;
	}
	
	return fieldConfig.fields.find(field => field.key === key);
}

/**
 * Check if a field should be visible based on configuration and conditions
 */
export function isFieldVisible(fieldConfig, fieldKey, addrPlusEnabled = false) {
	const field = getFieldByKey(fieldConfig, fieldKey);
	
	if (!field) {
		// If no config, use default visibility
		const addrPlusFields = ['parcel_no', 'lot_no', 'block_no'];
		if (addrPlusFields.includes(fieldKey)) {
			return addrPlusEnabled;
		}
		return true;
	}
	
	// Handle addr_plus fields specially
	if (['parcel_no', 'lot_no', 'block_no'].includes(fieldKey)) {
		return field.visible && addrPlusEnabled;
	}
	
	return field.visible;
}

/**
 * Get default empty address object with all possible fields
 */
export function getEmptyAddress(typeId = null) {
	const address = {
		company: '',
		name: '',
		phone: '',
		address: '',
		address_2: '',
		city: '',
		state: '',
		zip: '',
		county: '',
		email: '',
		parcel_no: '',
		lot_no: '',
		block_no: ''
	};

	// Add bond_no for bonding companies
	if (typeId === 10 || typeId === 11 || typeId === 12) {
		address.bond_no = '';
	}

	return address;
}

/**
 * Merge field configuration with defaults
 * Useful when config is partially defined or outdated
 * Works with both DB format (field_configs array) and component format
 */
export function mergeWithDefaults(savedConfig, typeId) {
	const defaults = getDefaultFieldsForType(typeId);
	
	if (!savedConfig || !savedConfig.fields) {
		return { fields: defaults };
	}

	const configMap = new Map(savedConfig.fields.map(f => [f.key, f]));
	
	const mergedFields = defaults.map(defaultField => {
		const savedField = configMap.get(defaultField.key);
		if (savedField) {
			// Merge saved config with defaults, but preserve system requirements
			return {
				...defaultField,
				...savedField,
				systemRequired: defaultField.systemRequired,
				defaultLabel: defaultField.defaultLabel
			};
		}
		return defaultField;
	});

	return { fields: mergedFields };
}

/**
 * Merge database field_configs with defaults
 * Ensures all default fields exist, even if not in DB
 */
export function mergeDbConfigsWithDefaults(dbConfigs, typeId) {
	const defaults = getDefaultFieldsForType(typeId);
	
	if (!dbConfigs || dbConfigs.length === 0) {
		return defaults;
	}
	
	const configMap = new Map(dbConfigs.map(c => [c.key, c]));
	
	const mergedFields = defaults.map(defaultField => {
		const dbConfig = configMap.get(defaultField.key);
		if (dbConfig) {
			// Use DB config but preserve system requirements from defaults
			return {
				...defaultField,
				id: dbConfig.id,
				label: dbConfig.label,
				placeholder: dbConfig.placeholder || '',
				visible: dbConfig.visible,
				required: dbConfig.required,
				order: dbConfig.order,
				width: dbConfig.width || 'col',
				helpText: dbConfig.help_text || '',
				minLength: dbConfig.min_length,
				maxLength: dbConfig.max_length,
				pattern: dbConfig.pattern || '',
				systemRequired: defaultField.systemRequired,
				defaultLabel: dbConfig.default_label || defaultField.defaultLabel
			};
		}
		return defaultField;
	});
	
	return mergedFields;
}

/**
 * Get default field configuration based on type_id
 * (Duplicated from AddressConfigurator for utility access)
 */
export function getDefaultFieldsForType(typeId) {
	const baseFields = [
		{
			key: 'company',
			type: 'text',
			label: 'Company Name',
			defaultLabel: 'Company Name',
			placeholder: '',
			visible: true,
			required: true,
			systemRequired: typeId !== 2,
			order: 0,
			width: 'col'
		},
		{
			key: 'name',
			type: 'text',
			label: 'Contact Name',
			defaultLabel: 'Contact Name',
			placeholder: '',
			visible: true,
			required: false,
			systemRequired: false,
			order: 1,
			width: 'col-md-6'
		},
		{
			key: 'phone',
			type: 'phone',
			label: 'Phone',
			defaultLabel: 'Phone',
			placeholder: '',
			visible: true,
			required: false,
			systemRequired: false,
			order: 2,
			width: 'col-md-6',
			minLength: 7,
			maxLength: 50,
			pattern: '^[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$'
		},
		{
			key: 'address',
			type: 'text',
			label: 'Street Address',
			defaultLabel: 'Street Address',
			placeholder: '',
			visible: true,
			required: true,
			systemRequired: true,
			order: 3,
			width: 'col'
		},
		{
			key: 'address_2',
			type: 'text',
			label: 'Street Address 2',
			defaultLabel: 'Street Address 2',
			placeholder: 'Apt, Suite, Building, Floor, etc.',
			visible: true,
			required: false,
			systemRequired: false,
			order: 4,
			width: 'col'
		},
		{
			key: 'city',
			type: 'text',
			label: 'City',
			defaultLabel: 'City',
			placeholder: '',
			visible: true,
			required: true,
			systemRequired: true,
			order: 5,
			width: 'col-md-4'
		},
		{
			key: 'state',
			type: 'dropdown',
			label: 'State',
			defaultLabel: 'State',
			placeholder: '',
			visible: true,
			required: typeId === 2,
			systemRequired: typeId === 2,
			order: 6,
			width: 'col-md-2'
		},
		{
			key: 'zip',
			type: 'text',
			label: 'Zip',
			defaultLabel: 'Zip',
			placeholder: '',
			visible: true,
			required: true,
			systemRequired: true,
			order: 7,
			width: 'col-md-3'
		},
		{
			key: 'county',
			type: 'text',
			label: 'County',
			defaultLabel: 'County',
			placeholder: '',
			visible: true,
			required: false,
			systemRequired: false,
			order: 8,
			width: 'col-md-3'
		},
		{
			key: 'email',
			type: 'email',
			label: 'Email Address',
			defaultLabel: 'Email Address',
			placeholder: '',
			visible: true,
			required: false,
			systemRequired: false,
			order: 9,
			width: 'col',
			pattern: '[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$'
		},
		{
			key: 'parcel_no',
			type: 'text',
			label: 'Parcel Number',
			defaultLabel: 'Parcel Number',
			placeholder: '',
			visible: false,
			required: false,
			systemRequired: false,
			order: 10,
			width: 'col'
		},
		{
			key: 'lot_no',
			type: 'text',
			label: 'Lot Number',
			defaultLabel: 'Lot Number',
			placeholder: '',
			visible: false,
			required: false,
			systemRequired: false,
			order: 11,
			width: 'col-md-6'
		},
		{
			key: 'block_no',
			type: 'text',
			label: 'Block Number',
			defaultLabel: 'Block Number',
			placeholder: '',
			visible: false,
			required: false,
			systemRequired: false,
			order: 12,
			width: 'col-md-6'
		}
	];

	// Add bond_no for bonding companies
	if (typeId === 10 || typeId === 11 || typeId === 12) {
		baseFields.unshift({
			key: 'bond_no',
			type: 'text',
			label: 'Bond Number',
			defaultLabel: 'Bond Number',
			placeholder: '',
			visible: true,
			required: false,
			systemRequired: false,
			order: -1,
			width: 'col',
			maxLength: BOND_NO_MAX_LENGTH
		});
	}

	// Adjust for project location (type_id = 2)
	if (typeId === 2) {
		return baseFields.map(field => {
			if (['company', 'name', 'phone', 'email'].includes(field.key)) {
				return { ...field, visible: false, required: false };
			}
			return field;
		});
	}

	// Adjust for null type_id (customer)
	if (typeId === null || typeId === 0) {
		return baseFields.map(field => {
			if (field.key === 'company') {
				return { ...field, label: 'Your Customer', defaultLabel: 'Your Customer' };
			}
			return field;
		});
	}

	return baseFields;
}

/**
 * Get label for field based on type_id and field key
 */
export function getFieldLabel(typeId, fieldKey, customLabel = null) {
	if (customLabel) return customLabel;
	
	const defaults = getDefaultFieldsForType(typeId);
	const field = defaults.find(f => f.key === fieldKey);
	return field ? field.label : fieldKey;
}

/**
 * Export field configuration to backend format (as array of field_config records)
 * @param {Object} fieldConfig - Field configuration with fields array
 * @param {Number} fieldId - Parent field ID
 * @returns {Array} - Array of field_config records for database
 */
export function exportConfigForBackend(fieldConfig, fieldId) {
	if (!fieldConfig || !fieldConfig.fields) {
		return [];
	}

	return transformStateToDbConfig(fieldConfig.fields, fieldId);
}
